import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';
import api from '../services/api';

const SettingsContext = createContext({});
export function useSettings() { return useContext(SettingsContext); }

// Parse helpers — handles JSONB values (true, false, "string", 123)
function parseNum(v, fallback) {
  if (v === undefined || v === null) return fallback;
  const n = parseFloat(String(v).replace(/[^0-9.]/g, ''));
  return isNaN(n) ? fallback : n;
}
function parseBool(v, fallback = false) {
  if (v === undefined || v === null) return fallback;
  if (v === true || v === 'true') return true;
  if (v === false || v === 'false') return false;
  return fallback;
}
function parseStr(v, fallback = '') {
  if (v === undefined || v === null) return fallback;
  return String(v).replace(/^"|"$/g, '').trim() || fallback;
}

export function SettingsProvider({ children }) {
  const [raw, setRaw] = useState({});
  const [loaded, setLoaded] = useState(false);

  const refresh = useCallback(async () => {
    try {
      const r = await api.get('/system/settings/public');
      setRaw(r.data || {});
      setLoaded(true);
    } catch { setLoaded(true); }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  const ctx = {
    loaded,
    refresh,
    // Branding
    platformName:        parseStr(raw.platform_name, 'Invest Zone'),
    platformLogo:        parseStr(raw.platform_logo, ''),
    platformFavicon:     parseStr(raw.platform_favicon, ''),
    platformCurrency:    parseStr(raw.platform_currency, 'INR'),
    telegramUrl:         parseStr(raw.telegram_support_url, ''),
    // Financial limits
    minRecharge:         parseNum(raw.min_recharge, 500),
    minWithdrawal:       parseNum(raw.min_withdrawal, 1000),
    maxWithdrawal:       parseNum(raw.max_withdrawal, 500000),
    withdrawalFee:       parseNum(raw.withdrawal_fee_percent, 5),
    // Service on/off  (true = service is DISABLED / blocked)
    rechargeDisabled:    parseBool(raw.recharge_disabled, false),
    withdrawalDisabled:  parseBool(raw.withdrawal_disabled, false),
    registrationDisabled: parseBool(raw.registration_disabled, false),
    // Feature flags (true = feature is ENABLED)
    spinEnabled:         parseBool(raw.spin_enabled, true),
    streakEnabled:       parseBool(raw.streak_enabled, true),
    tasksEnabled:        parseBool(raw.tasks_enabled, true),
    transferEnabled:     parseBool(raw.wallet_transfer_enabled, true),
    pushEnabled:         parseBool(raw.push_notifications_enabled, true),
    // Raw access for anything else
    raw,
  };

  return <SettingsContext.Provider value={ctx}>{children}</SettingsContext.Provider>;
}
